import { isValidString } from '../common/CommonUtilsMethods';
import {
    VERIFICATION_RESEND_COOLDOWN_SECONDS,
    VERIFICATION_RESEND_COOLDOWN_SESSION_KEY_PREFIX,
    VerificationResendCooldownScope,
} from './WorkyVerificationResendCooldownInterfaces';

const buildVerificationResendCooldownSessionKey = ({
    cooldownScope,
    emailAddress,
}: {
    cooldownScope: VerificationResendCooldownScope;
    emailAddress: string;
}): string => {
    const normalizedEmailAddress: string = emailAddress.trim().toLowerCase();
    return `${VERIFICATION_RESEND_COOLDOWN_SESSION_KEY_PREFIX}_${cooldownScope}_${normalizedEmailAddress}`;
};

const readResendCooldownSecondsRemaining = ({
    cooldownScope,
    emailAddress,
}: {
    cooldownScope: VerificationResendCooldownScope;
    emailAddress: string;
}): number => {
    if (!isValidString(emailAddress)) {
        return 0;
    }

    const cooldownSessionKey: string = buildVerificationResendCooldownSessionKey({
        cooldownScope,
        emailAddress,
    });
    const cooldownEndEpochMillisecondsRaw: string | null = sessionStorage.getItem(cooldownSessionKey);
    if (!isValidString(cooldownEndEpochMillisecondsRaw)) {
        return 0;
    }

    const cooldownEndEpochMilliseconds: number = Number.parseInt(
        cooldownEndEpochMillisecondsRaw,
        10
    );
    if (Number.isNaN(cooldownEndEpochMilliseconds)) {
        return 0;
    }

    const currentEpochMilliseconds: number = Date.now();
    const remainingMilliseconds: number = cooldownEndEpochMilliseconds - currentEpochMilliseconds;
    if (remainingMilliseconds <= 0) {
        sessionStorage.removeItem(cooldownSessionKey);
        return 0;
    }

    return Math.ceil(remainingMilliseconds / 1000);
};

const persistResendCooldownEndEpochMilliseconds = ({
    cooldownScope,
    emailAddress,
}: {
    cooldownScope: VerificationResendCooldownScope;
    emailAddress: string;
}): void => {
    if (!isValidString(emailAddress)) {
        return;
    }

    const cooldownEndEpochMilliseconds: number =
        Date.now() + VERIFICATION_RESEND_COOLDOWN_SECONDS * 1000;
    const cooldownSessionKey: string = buildVerificationResendCooldownSessionKey({
        cooldownScope,
        emailAddress,
    });
    sessionStorage.setItem(cooldownSessionKey, cooldownEndEpochMilliseconds.toString());
};

const clearResendCooldown = ({
    cooldownScope,
    emailAddress,
}: {
    cooldownScope: VerificationResendCooldownScope;
    emailAddress: string;
}): void => {
    if (!isValidString(emailAddress)) {
        return;
    }

    const cooldownSessionKey: string = buildVerificationResendCooldownSessionKey({
        cooldownScope,
        emailAddress,
    });
    sessionStorage.removeItem(cooldownSessionKey);
};

export {
    clearResendCooldown,
    persistResendCooldownEndEpochMilliseconds,
    readResendCooldownSecondsRemaining,
};
